import React, { useMemo, useState } from "react";
import { useParams, useRouteMatch } from "react-router-dom";
import { Button, Card, FullscreenTakeover, QueryLoader, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { MISSING_VALUE_PLACEHOLDER } from "../../components/common/constants";
import { DefaultModal } from "../../components/common/DefaultModal";
import { isDocumentExpired } from "../../components/documents/utils";
import { ProfileRefreshCard } from "../../components/landingscreens/ProfileRefreshCard";
import { ProfileRefreshCompletionCard } from "../../components/landingscreens/ProfileRefreshCompletionCard";
import { provideLegalName } from "../../components/ProfileTree/utils";
import {
  ComplianceCaseAction,
  ComplianceCaseAuditTagType,
  ComplianceCaseTypeEnum,
  ComplianceExternalObjectType,
} from "../../graphql/supergraphEnums";
import {
  usePerformActionMutation,
  useGetUserComplianceCasesQuery,
} from "../../graphql/supergraphHooks";
import { resolvedAccountType } from "../../utils/account_types";
import { clientContext } from "../../utils/graphql_utils";
import { StandAloneCompliance } from "../StandAloneCompliance";
import { buildComponentContext } from "../StandAloneCompliance/utils";
import { LandingUIState } from "../types";
import { navigationButton, contentWrapper } from "./styles_vanilla.css";
import { findRefreshUIState } from "./utils";

export type ComplianceRefreshComponentProps = {
  arenaId: number;
  errorComponent: React.JSX.Element;
};

export const ComplianceRefreshComponent: React.FC<ComplianceRefreshComponentProps> = ({
  arenaId,
  errorComponent,
}) => {
  const { profileGuid } = useParams<{ profileGuid: string }>();
  const { url: basePath } = useRouteMatch();
  const [showPartialSubmissionModal, setShowPartialSubmissionModal] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [completedOrgName, setCompletedOrgName] = useState("");

  const result = useGetUserComplianceCasesQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      caseType: ComplianceCaseTypeEnum.ProfileRefresh,
      externalObject: {
        objectTypeEnum: ComplianceExternalObjectType.ComplianceProfile,
        objectId: profileGuid,
        service: "main",
      },
      auditTag: ComplianceCaseAuditTagType.CaseSubmitted,
    },
  });

  const [performAction, { loading: performActionLoading }] = usePerformActionMutation({
    context: clientContext(),
    refetchQueries: ["GetUserComplianceCases"],
    awaitRefetchQueries: true,
  });

  const complianceCase = result.data?.userAccessibleComplianceCases[0];
  const investingProfile = complianceCase?.investingProfile;
  const accountType = resolvedAccountType(investingProfile);
  const orgName = useMemo(() => provideLegalName(investingProfile), [investingProfile]);
  const lastRefreshDate = investingProfile?.refreshSchedule?.lastRefreshDate;
  const hasExpiredDocuments = useMemo(
    () =>
      (investingProfile?.documentRefreshSchedules ?? []).some((doc) =>
        isDocumentExpired(doc.expiryDate),
      ),
    [investingProfile?.documentRefreshSchedules],
  );

  const componentContext = buildComponentContext(arenaId, basePath, true, {
    id: complianceCase?.id,
    caseType: complianceCase?.caseType,
    investingProfileId: complianceCase?.investingProfile?.id,
    programId: complianceCase?.programId,
  });

  const refreshUIState = useMemo(
    () =>
      showCompletion
        ? LandingUIState.Completion
        : findRefreshUIState(performActionLoading, complianceCase),
    [complianceCase, performActionLoading, showCompletion],
  );

  const goToPortalHome = () => {
    window.location.href = "/lp/home";
  };

  const handleExitClick = () => {
    if (refreshUIState === LandingUIState.Compliance) {
      setShowPartialSubmissionModal(true);
    } else {
      goToPortalHome();
    }
  };

  const handleContinue = (hasChanges?: boolean) => {
    const action =
      hasChanges === true
        ? ComplianceCaseAction.StartSubmission
        : hasChanges === false
          ? ComplianceCaseAction.CompleteNoChange
          : ComplianceCaseAction.CompleteSubmission;

    void performAction({
      variables: {
        caseId: Number(complianceCase?.id),
        action,
      },
    })
      .then((result) => {
        if (result.data && action !== ComplianceCaseAction.StartSubmission) {
          setCompletedOrgName(orgName);
          setShowCompletion(true);
        }
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : "An error occurred");
      });
  };

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        leftActionContent={
          <Button
            variant="neutral"
            icon="xmark"
            iconPosition="left"
            iconVariant="outline"
            className={navigationButton}
            onClick={handleExitClick}
          >
            Exit
          </Button>
        }
        title={
          orgName !== MISSING_VALUE_PLACEHOLDER
            ? `${orgName} Refresh`
            : MISSING_VALUE_PLACEHOLDER
        }
        rightActionContent={
          <Icon
            type="question-circle"
            variant="outline"
            className={navigationButton}
            onClick={() => {
              window.open("https://investor-help.junipersquare.com/hc/en-us", "_blank");
            }}
          />
        }
      />
      <QueryLoader result={result} renderError={() => errorComponent}>
        <FullscreenTakeover.Body>
          {refreshUIState === LandingUIState.CaseNotFound ? (
            errorComponent
          ) : (
            <Card className={contentWrapper}>
              {/* Landing screen */}
              {refreshUIState === LandingUIState.Confirmation && (
                <ProfileRefreshCard
                  orgName={orgName}
                  accountType={accountType}
                  lastRefreshDate={lastRefreshDate}
                  onContinue={handleContinue}
                  onSkip={goToPortalHome}
                  isLoading={performActionLoading}
                  isExpired={hasExpiredDocuments}
                />
              )}

              {/* Completion screen */}
              {refreshUIState === LandingUIState.Completion && (
                <ProfileRefreshCompletionCard
                  accountName={completedOrgName || orgName}
                  onClose={goToPortalHome}
                />
              )}

              {/* Profile Tree screen */}
              {refreshUIState === LandingUIState.Compliance && (
                <StandAloneCompliance
                  componentContext={componentContext}
                  onStepCompletion={() => handleContinue()}
                />
              )}
            </Card>
          )}
        </FullscreenTakeover.Body>
      </QueryLoader>
      <DefaultModal
        show={showPartialSubmissionModal}
        title="Warning"
        body="Are you sure you want to exit to portal? Any unsaved information will be lost."
        posBtnText="Continue"
        posBtnVariant="primary"
        negBtnText="Cancel"
        onClose={() => setShowPartialSubmissionModal(false)}
        onConfirm={() => {
          setShowPartialSubmissionModal(false);
          goToPortalHome();
        }}
      />
    </FullscreenTakeover>
  );
};
